import { mdiCheck } from '@mdi/js';
import _ from 'lodash';
import React from 'react';
import * as S from './claim-timeline-stepper.styles';

export enum ClaimTimelineStep {
    RECORD = 1,
    CREATE = 2,
    SUBMIT = 3,
    RECEIVE = 5,
}

export type StepperState = 'complete' | 'current' | 'pending' | 'skipped';

interface ITimelineItem {
    step: ClaimTimelineStep;
    label1: string;
    label2: string;
    label3: string;
}
interface IClaimTimelineStepperProps {
    step?: ClaimTimelineStep;
}
interface IClaimTimelineStepperState {
    clientWidth: number;
    clientRect?: ClientRect;
}

const timelineItems: ITimelineItem[] = [
    {
        step: ClaimTimelineStep.RECORD,
        label1: 'Record',
        label2: 'Patient',
        label3: 'Visit',
    },
    {
        step: ClaimTimelineStep.CREATE,
        label1: 'Create',
        label2: 'Claim',
        label3: '',
    },
    {
        step: ClaimTimelineStep.SUBMIT,
        label1: 'Submit',
        label2: 'Claim',
        label3: '',
    },
    {
        step: ClaimTimelineStep.RECEIVE,
        label1: 'Receive',
        label2: 'Responses',
        label3: '',
    },
];

export class ClaimTimelineStepper extends React.Component<IClaimTimelineStepperProps, IClaimTimelineStepperState> {
    private svgRef: React.RefObject<SVGSVGElement>;

    constructor(props: IClaimTimelineStepperProps) {
        super(props);
        this.svgRef = React.createRef();
        this.state = {
            clientWidth: 0,
        };
    }
    public async componentDidMount(): Promise<void> {
        window.addEventListener('resize', this.updateClientRect);
        this.updateClientRect();
    }
    public async componentDidUpdate(): Promise<void> {
        this.updateClientRect();
    }
    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateClientRect);
    }
    public render(): React.ReactElement {
        return (
            <svg ref={this.svgRef} width="100%" height="100%">
                {this.renderStepper()}
            </svg>
        );
    }
    private readonly renderStepper = (): React.ReactElement => {
        if (this.state.clientWidth) {
            const { step } = this.props;
            const { clientWidth } = this.state;
            const count = timelineItems.length;
            const unit = clientWidth / (count + 1);

            const left = unit * 0.7;
            const right = left + (count - 1) * unit;
            const middle = 50;

            const offset = step ? (step as number) : -1;

            const lines = [
                <S.LineBg key="lineBg" x1={left} y1={middle} x2={right} y2={middle} />,
                offset !== -1 ? (
                    <S.LineFg
                        key="lineFg"
                        x1={left}
                        y1={middle}
                        x2={Math.min(right, left + offset * unit)}
                        y2={middle}
                    />
                ) : null,
            ];
            const circles = _.times(count, (i: number) => {
                let type: StepperState;
                if (i === offset) {
                    type = 'current';
                } else if (i < offset) {
                    type = 'complete';
                } else {
                    type = 'pending';
                }
                return <S.Circle key={`c${i}`} type={type} cx={left + unit * i} cy={middle} r={i < offset ? 12 : 8} />;
            });
            const icons = _.compact(
                _.times(offset, (i: number) => {
                    const checkScale = 0.85;
                    const checkSize = 12 * checkScale;
                    return (
                        <S.Icon
                            key={`i${i}`}
                            d={mdiCheck}
                            style={{
                                transform: `
                            translate(${left + unit * i - checkSize}px, ${middle - checkSize}px)
                            scale(${checkScale}, ${checkScale})
                            `,
                            }}
                        />
                    );
                }),
            );
            const text = _.times(count, (i: number) => {
                const item = timelineItems[i];
                const shouldWrapLine3 = unit < 85;
                return (
                    <g key={`g${i}`}>
                        <S.Step
                            type={i === offset ? 'current' : i < offset ? 'complete' : 'pending'}
                            x={left + unit * i - 9}
                            y={middle + 37}>
                            {`step ${i + 1}`}
                        </S.Step>
                        <S.Label
                            type={i === offset ? 'current' : i < offset ? 'complete' : 'pending'}
                            x={left + unit * i - 9}
                            y={middle + 59}>
                            {item.label1}
                        </S.Label>
                        <S.Label
                            type={i === offset ? 'current' : i < offset ? 'complete' : 'pending'}
                            x={left + unit * i - 9}
                            y={middle + 79}>
                            {item.label2 + (shouldWrapLine3 ? '' : ` ${item.label3}`)}
                        </S.Label>
                        {shouldWrapLine3 && (
                            <S.Label
                                type={i === offset ? 'current' : i < offset ? 'complete' : 'pending'}
                                x={left + unit * i - 9}
                                y={middle + 99}>
                                {item.label3}
                            </S.Label>
                        )}
                    </g>
                );
            });
            return (
                <g>
                    {lines}
                    {circles}
                    {icons}
                    {text}
                </g>
            );
        }
        return <></>;
    };
    private readonly updateClientRect = (): void => {
        if (this.svgRef && this.svgRef.current) {
            const clientRect = this.svgRef.current.getBoundingClientRect();
            const { width } = clientRect;
            if (width !== this.state.clientWidth) {
                this.setState({
                    clientWidth: width,
                });
            }
        }
    };
}
